import {Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {DipRegistrationLoggerService} from './logger/dip-registration-logger.service';
import {WebMetricsService} from './web-metrics/web-metrics.service';
import {PageName, WebMetricsEvent} from './web-metrics/web-metrics.constants';
import {getWebMetricsData} from './web-metrics/web-metrics-data.map';
import {DipRegistrationContextService} from './dip-registration-context.service';
import {WebMetricStore} from './stores/dip-web-metric-store.service';

declare let window: Window & typeof globalThis & {
  cdApi: { changeContext(data: string) };
};

@Injectable()
export class DipRegistrationNavigationService {
  isSessionExpired: boolean;
  private previousUrl: string = undefined;
  private currentUrl: string = undefined;

  constructor(
    private dipRegistrationContextService: DipRegistrationContextService,
    private webMetricsService: WebMetricsService,
    private loggerService: DipRegistrationLoggerService,
    private router: Router,
    private webMetricStore: WebMetricStore
  ) {
  }

  setSessionExpiredFlag() {
    this.isSessionExpired = true;
  }

  getSessionExpiredFlag(): boolean {
    return this.isSessionExpired;
  }

  private getNavigationData(): any {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }

    return route?.snapshot?.data || {};
  }

  private changeCdApiContext(data: string): void {
    if (window?.cdApi?.changeContext) {
      window.cdApi.changeContext(data);
    } else {
      this.loggerService.error('BioCatch change context does not exist');
    }
  }

  private handleBioCatchChangeContext(url: string): void {
    if (url) {
      switch (url.split('?')[0]) {
        case '/customer-identification':
          this.changeCdApiContext('seamless-enrollment-customer-info-page');
          break;
        case '/phone-number-list':
          this.changeCdApiContext('seamless-enrollment-confirm-your-number');
          break;
        case '/overview':
          this.changeCdApiContext('seamless-enrollment-checklist-page');
          break;
        case '/existing-account':
          this.changeCdApiContext('seamless-enrollment-account-already-exits');
          break;
        case '/session-expired':
          this.changeCdApiContext('seamless-enrollment-session-expired');
          break;
      }
    }
  }

  async init() {
        this.currentUrl = this.router.url;
        this.router.events.pipe(
          filter(event => event instanceof NavigationEnd)
        ).subscribe(async (event: NavigationEnd) => {
          this.previousUrl = this.currentUrl;
          this.currentUrl = event.url;
          const data = this.getNavigationData();
          if (data) {
            const tagInformation = data.tagInformation;
            const webMetricsData = getWebMetricsData(tagInformation);
            switch (webMetricsData?.page?.name) {
              case PageName.OVERVIEW:
                this.loggerService.info('Navigating to Overview page');
                this.webMetricsService.submit({
                  event: WebMetricsEvent.PAGE_CHANGE,
                  page: {name: webMetricsData.page.name},
                  site: this.webMetricsService.getInitialWebMetricsContext(),
                  process: webMetricsData?.process,
                  user: {custID: this.dipRegistrationContextService.getClientId()}
                });
                break;
              case PageName.CUSTOMER_IDENTIFICATION:
                this.loggerService.info('Navigating to Customer Identification page');
                this.webMetricsService.submit({
                  event: WebMetricsEvent.PAGE_CHANGE,
                  page: {name: webMetricsData.page.name},
                  process: {name: webMetricsData?.process.name}
                });
                break;
              case PageName.PHONE_NUMBER_LIST:
                this.loggerService.info('Navigating to Phone Number page');
                this.webMetricsService.submit({
                  event: WebMetricsEvent.PAGE_CHANGE,
                  page: {name: webMetricsData.page.name, entryPoint: this.webMetricStore.state.launchFrom},
                  process: {name: webMetricsData?.process.name}
                });
                break;
              case PageName.EXISTING_ACCOUNT_ERROR:
                this.loggerService.info('Navigating to Existing Account Error page');
                this.webMetricsService.submitErrorAndWarningTag('existingAccountTag');
                break;
              case PageName.SESSION_EXPIRED:
                this.loggerService.info('Navigating to Session Expired page');
                this.webMetricsService.submitErrorAndWarningTag('sessionExpiredTag');
                break;
              default:
                this.loggerService.warn(`No specific web metrics data for tag: ${tagInformation}`);
            }
          }

      this.handleBioCatchChangeContext(event.url);
    });
  }

  getPreviousUrl() {
    return this.previousUrl;
  }
}
